import {
  errorText,
  isAccessFailure,
  ProcessError,
  processService,
} from '@/pages/ActivityCenter/ActivityWorkflow/service';
import {
  getAuthSession,
  getAuthSessionGeneration,
  subscribeAuthSession,
} from './authSession';

export type ActivityProcessEntrySnapshot = Readonly<{
  ownerId?: string;
  generation: number;
  status: 'idle' | 'loading' | 'ready' | 'error';
  data?: CoreAPI.ActivityProcessEntry;
  error?: string;
  denied?: boolean;
  expiresAt: number;
}>;

interface RequestScope {
  ownerId: string;
  generation: number;
  revision: number;
}

const CACHE_TTL = 60_000;
const listeners = new Set<() => void>();
let revision = 0;
let pending:
| { scope: RequestScope; promise: Promise<CoreAPI.ActivityProcessEntry> }
| undefined;

function sessionIdentity() {
  // A username or a legacy userid fallback cannot identify an authorization owner.
  const id = getAuthSession()?.currentUser?.id;
  return {
    ownerId: typeof id === 'string' && id.trim() ? id : undefined,
    generation: getAuthSessionGeneration(),
  };
}

let snapshot: ActivityProcessEntrySnapshot = {
  ...sessionIdentity(),
  status: 'idle',
  expiresAt: 0,
};

function publish(next: ActivityProcessEntrySnapshot) {
  snapshot = next;
  for (const listener of listeners) listener();
}

function synchronizeSession() {
  const identity = sessionIdentity();
  if (
    identity.ownerId !== snapshot.ownerId ||
    identity.generation !== snapshot.generation
  ) {
    revision += 1;
    pending = undefined;
    publish({ ...identity, status: 'idle', expiresAt: 0 });
  }
}

// This resource lives for the application session, independently of its consumers.
subscribeAuthSession(synchronizeSession);

export function getActivityProcessEntrySnapshot(): ActivityProcessEntrySnapshot {
  synchronizeSession();
  return snapshot;
}

export function subscribeActivityProcessEntry(listener: () => void) {
  listeners.add(listener);
  synchronizeSession();
  return () => {
    listeners.delete(listener);
  };
}

export function invalidateActivityProcessEntry(): void {
  synchronizeSession();
  revision += 1;
  pending = undefined;
  publish({
    ...sessionIdentity(),
    status: 'idle',
    data: snapshot.data,
    expiresAt: 0,
  });
}

/** Refresh after an applied permission change; failures remain in the shared snapshot. */
export async function revalidateActivityProcessEntry(): Promise<
CoreAPI.ActivityProcessEntry | undefined
> {
  invalidateActivityProcessEntry();
  return loadActivityProcessEntry().catch(() => undefined);
}

function isCurrent(scope: RequestScope) {
  synchronizeSession();
  return (
    scope.ownerId === snapshot.ownerId &&
    scope.generation === snapshot.generation &&
    scope.revision === revision
  );
}

function assertCurrent(scope: RequestScope) {
  if (!isCurrent(scope)) throw new Error('流程入口查询已失效，请重新查询');
}

/**
 * Share a short-lived entry result and in-flight query across app and page consumers.
 * Only a fresh ready snapshot can authorize actions; retained data is a display hint.
 */
export function loadActivityProcessEntry(
  options: { force?: boolean } = {},
): Promise<CoreAPI.ActivityProcessEntry> {
  synchronizeSession();
  const { ownerId, generation } = snapshot;
  if (!ownerId) {
    const error = new ProcessError(
      '登录身份未确认，请重新登录',
      'UNAUTHORIZED',
      true,
    );
    if (snapshot.status !== 'error' || !snapshot.denied) {
      publish({
        generation,
        status: 'error',
        error: error.message,
        denied: true,
        expiresAt: 0,
      });
    }
    return Promise.reject(error);
  }
  if (pending) return pending.promise;
  const scope = { ownerId, generation, revision };
  if (
    !options.force &&
    snapshot.status === 'ready' &&
    snapshot.data &&
    snapshot.expiresAt > Date.now()
  ) {
    const cached = snapshot.data;
    return Promise.resolve().then(() => {
      assertCurrent(scope);
      return cached;
    });
  }

  const previousData = snapshot.data;
  // Install pending before notifying subscribers, which may synchronously load again.
  const promise = Promise.resolve().then(async () => {
    try {
      assertCurrent(scope);
      const data = await processService.entry({});
      assertCurrent(scope);
      if (typeof data.visible !== 'boolean') throw new Error('服务未返回完整的流程入口信息，请重试查询');
      if (pending?.scope === scope) pending = undefined;
      publish({
        ownerId,
        generation,
        status: 'ready',
        data,
        expiresAt: Date.now() + CACHE_TTL,
      });
      // A synchronous subscriber may have invalidated this result while notified.
      assertCurrent(scope);
      return data;
    } catch (error) {
      if (isCurrent(scope)) {
        const denied = isAccessFailure(error);
        if (pending?.scope === scope) pending = undefined;
        publish({
          ownerId,
          generation,
          status: 'error',
          data: denied ? undefined : previousData,
          error: errorText(error),
          denied,
          expiresAt: 0,
        });
      }
      throw error;
    } finally {
      if (pending?.scope === scope) pending = undefined;
    }
  });
  pending = { scope, promise };
  publish({
    ownerId,
    generation,
    status: 'loading',
    data: previousData,
    expiresAt: 0,
  });
  return promise;
}
